import { useEffect, useRef, useState } from 'react';

const BEFORE_IMG = 'https://www.smartcap.co.kr/assets/images/%EC%8B%9C%EA%B3%B5%EC%A0%84.png';
const AFTER_IMG = 'https://www.smartcap.co.kr/assets/images/%EC%8B%9C%EA%B3%B5%ED%9B%84.png';
const VIDEO_SRC_1 = 'https://www.smartcap.co.kr/assets/videos/%EC%8B%9C%EA%B3%B5%EC%98%81%EC%83%81.mp4';
const VIDEO_SRC_2 = 'https://www.smartcap.co.kr/assets/videos/%EC%8B%9C%EA%B3%B5%EC%98%81%EC%83%812.mp4';

const steps = [
  {
    num: '01',
    title: '위치 선정',
    desc: '샌드위치 패널의 산 부분에 스마트캡을 올려 위치시킵니다.',
    bgColor: '#1B2A4A',
    textColor: '#FF6B35',
    descColor: '#CBD5E1',
    numColor: '#FF6B35',
  },
  {
    num: '02',
    title: '볼트 체결',
    desc: '전동드릴로 볼트를 체결합니다. 별도의 실리콘 도포 작업이 필요 없습니다.',
    bgColor: '#FF6B35',
    textColor: '#ffffff',
    descColor: '#FFF0E8',
    numColor: '#ffffff',
  },
  {
    num: '03',
    title: '자동 방수 완성',
    desc: '체결 시 내장 실리콘 패킹이 자동으로 눌리며 밀폐형 방수 구조가 형성됩니다.',
    bgColor: '#1B2A4A',
    textColor: '#FF6B35',
    descColor: '#CBD5E1',
    numColor: '#FF6B35',
  },
  {
    num: '04',
    title: '시공 완료',
    desc: '추가 실리콘 작업 없이 시공이 완료됩니다.',
    bgColor: '#FF6B35',
    textColor: '#ffffff',
    descColor: '#FFF0E8',
    numColor: '#ffffff',
  },
];

const stats = [
  { value: '3초', label: '개당 시공 시간', sub: '기존 대비 3배 빠름' },
  { value: '0%', label: '누수 발생률', sub: '테스트 기준' },
  { value: '52%', label: '인건비 절감', sub: '기존 25만원→12만원/일' },
];

function useScrollAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

export default function InstallationSection() {
  const header = useScrollAnimation();
  const images = useScrollAnimation();
  const video = useScrollAnimation();
  const cards = useScrollAnimation();
  const banner = useScrollAnimation();
  const statsRow = useScrollAnimation();

  return (
    <section className="bg-gray-50 py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* A. 헤더 */}
        <div
          ref={header.ref}
          className={'text-center mb-14 transition-all duration-700 ' + (header.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8')}
        >
          <span
            className="inline-block text-xs font-bold tracking-widest px-4 py-2 rounded-full mb-4"
            style={{ backgroundColor: '#FF6B35', color: '#ffffff' }}
          >
            시공 방법
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-3" style={{ color: '#1B2A4A' }}>
            단 4단계로 완성되는 방수 시공
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
            복잡한 실리콘 도포 작업 없이 누구나 쉽게 시공할 수 있습니다.
          </p>
        </div>

        {/* B. 시공 전후 이미지 비교 */}
        <div
          ref={images.ref}
          className={'grid grid-cols-1 md:grid-cols-2 gap-6 mb-14 transition-all duration-700 ' + (images.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8')}
        >
          <div className="rounded-2xl overflow-hidden shadow-md">
            <div
              className="text-center text-sm font-bold py-2"
              style={{ backgroundColor: '#374151', color: '#ffffff' }}
            >
              시공 전 (BEFORE)
            </div>
            <div
              className="w-full h-80 md:h-96"
              style={{
                backgroundImage: 'url(' + BEFORE_IMG + ')',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <img
                src={BEFORE_IMG}
                alt="스마트캡 시공 전"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-md">
            <div
              className="text-center text-sm font-bold py-2"
              style={{ backgroundColor: '#1B2A4A', color: '#FF6B35' }}
            >
              시공 후 (AFTER)
            </div>
            <div
              className="w-full h-80 md:h-96"
              style={{
                backgroundImage: 'url(' + AFTER_IMG + ')',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <img
                src={AFTER_IMG}
                alt="스마트캡 시공 후"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* C. 시공 영상 */}
        <div
          ref={video.ref}
          className={'mb-14 transition-all duration-700 ' + (video.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8')}
        >
          <h3 className="text-xl font-bold text-center mb-5" style={{ color: '#1B2A4A' }}>
            시공 영상 보기
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-md">
              <video
                src={VIDEO_SRC_1}
                controls
                className="w-full"
                style={{ display: 'block' }}
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md">
              <video
                src={VIDEO_SRC_2}
                controls
                className="w-full"
                style={{ display: 'block' }}
              />
            </div>
          </div>
        </div>

        {/* D. 4단계 시공 과정 카드 */}
        <div
          ref={cards.ref}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14"
        >
          {steps.map((step, i) => (
            <div
              key={step.num}
              className={'rounded-2xl p-6 transition-all duration-700 ' + (cards.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8')}
              style={{
                backgroundColor: step.bgColor,
                transitionDelay: (i * 120) + 'ms',
              }}
            >
              <div
                className="text-4xl font-black mb-4"
                style={{ color: step.numColor }}
              >
                {step.num}
              </div>
              <h4
                className="text-lg font-bold mb-2"
                style={{ color: step.textColor }}
              >
                {step.title}
              </h4>
              <p className="text-sm leading-relaxed" style={{ color: step.descColor }}>
                {step.desc}
              </p>
            </div>
          ))}
        </div>

        {/* E. Before/After 텍스트 배너 */}
        <div
          ref={banner.ref}
          className={'rounded-2xl overflow-hidden mb-14 flex flex-col md:flex-row transition-all duration-700 ' + (banner.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8')}
        >
          {/* BEFORE */}
          <div className="flex-1 px-8 py-8" style={{ backgroundColor: '#374151' }}>
            <div className="text-xs font-bold tracking-widest mb-3" style={{ color: '#9CA3AF' }}>
              BEFORE
            </div>
            <div className="text-lg font-bold mb-5" style={{ color: '#F3F4F6' }}>
              기존 방식
            </div>
            <ul className="space-y-2">
              {['실리콘 도포', '경화 대기 (1~2시간)', '볼트 체결', '전 과정 반복'].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm" style={{ color: '#9CA3AF' }}>
                  <span className="line-through">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* 화살표 */}
          <div
            className="flex items-center justify-center px-6 py-6 md:py-0"
            style={{ backgroundColor: '#FF6B35' }}
          >
            <span className="text-3xl font-black text-white">→</span>
          </div>

          {/* AFTER */}
          <div className="flex-1 px-8 py-8" style={{ backgroundColor: '#1B2A4A' }}>
            <div className="text-xs font-bold tracking-widest mb-3" style={{ color: '#FF6B35' }}>
              AFTER
            </div>
            <div className="text-lg font-bold mb-5" style={{ color: '#ffffff' }}>
              스마트캡
            </div>
            <ul className="space-y-2">
              {['볼트 선반', '볼트 체결', '끝'].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm" style={{ color: '#CBD5E1' }}>
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: '#FF6B35', color: '#ffffff' }}
                  >
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* F. 통계 행 */}
        <div
          ref={statsRow.ref}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={'rounded-2xl px-8 py-8 text-center transition-all duration-700 ' + (statsRow.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8')}
              style={{
                backgroundColor: '#1B2A4A',
                transitionDelay: (i * 120) + 'ms',
              }}
            >
              <div className="text-4xl font-black mb-1" style={{ color: '#FF6B35' }}>
                {stat.value}
              </div>
              <div className="text-base font-bold text-white mb-1">{stat.label}</div>
              <div className="text-xs" style={{ color: '#94A3B8' }}>{stat.sub}</div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
